import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X, Search, Gift } from 'lucide-react';
import apiClient from '../../helpers/apiClient';

interface Student {
  id: number;
  name: string;
  avatar?: string;
  gift?: number;
  className?: string;
}

interface ChallengeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (accepterId: number, accepterName: string, giftAmount: number, gameType: 'quiz' | 'matching', challengerScore?: number) => void;
  gameType: 'quiz' | 'matching';
  currentStudentId: number;
  currentStudentGifts: number;
  challengerScore?: number; // Score to include when creating challenge
}

export const ChallengeModal: React.FC<ChallengeModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  gameType,
  currentStudentId,
  currentStudentGifts,
  challengerScore,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [giftAmount, setGiftAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Search students
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(() => {
      searchStudents();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const searchStudents = async () => {
    try {
      setLoading(true);
      setError('');
      // Search students by name
      const response = await apiClient.get('/api/students/search', {
        params: { name: searchQuery.trim() }
      });

      if (!response.data) throw new Error('Failed to search students');

      // Filter to exclude current student (backend already returns limited results)
      const filtered = response.data.filter((s: Student) => s.id !== currentStudentId);

      setSearchResults(filtered);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error searching students');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedStudent) {
      setError('Please select a student');
      return;
    }

    if (giftAmount < 0) {
      setError('Số sao không hợp lệ');
      return;
    }

    if (giftAmount > currentStudentGifts) {
      setError(`Bạn chỉ có ${currentStudentGifts} sao, số sao thách đấu phải nhỏ hơn số sao của bạn.`);
      return;
    }

    if (selectedStudent.gift === undefined || selectedStudent.gift === null) {
      setError('Không thể xác định số sao của đối thủ');
      return;
    }

    if (giftAmount > selectedStudent.gift) {
      setError(`Đối thủ chỉ có ${selectedStudent.gift} sao, số sao thách đấu phải nhỏ hơn số sao của đối thủ.`);
      return;
    }

    try {
      setLoading(true);
      onSubmit(selectedStudent.id, selectedStudent.name, giftAmount, gameType, challengerScore);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error submitting challenge');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>🎮 Thách Đấu {gameType === 'quiz' ? 'Trắc Nghiệm' : 'Ghép Thẻ'}</Title>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </Header>

        <Content>
          {error && <ErrorMessage>{error}</ErrorMessage>}

          <SearchContainer>
            <SearchIcon>
              <Search size={18} />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Tìm kiếm học sinh..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchContainer>

          {searchQuery.trim().length > 0 && (
            <SearchResults>
              {loading && <LoadingText>Đang tìm kiếm...</LoadingText>}
              {!loading && searchResults.length === 0 && (
                <EmptyText>Không tìm thấy học sinh nào</EmptyText>
              )}
              {searchResults.map((student) => (
                <StudentResult
                  key={student.id}
                  onClick={() => {
                    setSelectedStudent(student);
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                  $selected={selectedStudent?.id === student.id}
                >
                  {student.avatar && (
                    <Avatar src={student.avatar} alt={student.name} />
                  )}
                  <StudentInfo>
                    <StudentName>{student.name}</StudentName>
                    <StudentClass>Lớp: {student.className || 'Tự do'}</StudentClass>
                    <StudentGifts>
                      <Gift size={14} /> {student.gift || 0} sao
                    </StudentGifts>
                  </StudentInfo>
                </StudentResult>
              ))}
            </SearchResults>
          )}

          {selectedStudent && (
            <>
              <SelectedContainer>
                <Label>Đối thủ được chọn:</Label>
                <SelectedStudent>
                  {selectedStudent.avatar && (
                    <Avatar src={selectedStudent.avatar} alt={selectedStudent.name} />
                  )}
                  <StudentInfo>
                    <StudentName>{selectedStudent.name}</StudentName>
                    <StudentClass>Lớp: {selectedStudent.className || 'Tự do'}</StudentClass>
                    <StudentGifts>
                      <Gift size={14} /> {selectedStudent.gift || 0} sao
                    </StudentGifts>
                  </StudentInfo>
                </SelectedStudent>
              </SelectedContainer>

              <GiftSection>
                <Label>Số sao thách đấu:</Label>
                <GiftInputContainer>
                  <GiftButton
                    onClick={() =>
                      setGiftAmount(
                        Math.max(0, giftAmount - 1)
                      )
                    }
                    disabled={giftAmount <= 0}
                  >
                    −
                  </GiftButton>
                  <GiftInput
                    type="number"
                    value={giftAmount}
                    min={0}
                    max={selectedStudent ? Math.min(currentStudentGifts, selectedStudent.gift || 0) : currentStudentGifts}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      const maxGift = selectedStudent ? Math.min(currentStudentGifts, selectedStudent.gift || 0) : currentStudentGifts;
                      setGiftAmount(Math.min(Math.max(0, val), maxGift));
                    }}
                  />
                  <GiftButton
                    onClick={() => {
                      const maxGift = selectedStudent ? Math.min(currentStudentGifts, selectedStudent.gift || 0) : currentStudentGifts;
                      setGiftAmount(Math.min(giftAmount + 1, maxGift));
                    }}
                    disabled={giftAmount >= (selectedStudent ? Math.min(currentStudentGifts, selectedStudent.gift || 0) : currentStudentGifts) || (selectedStudent && giftAmount >= (selectedStudent.gift || 0))}
                  >
                    +
                  </GiftButton>
                </GiftInputContainer>
                <GiftInfo>
                  Bạn có: <strong>{currentStudentGifts}</strong> sao, Đối thủ có: <strong>{selectedStudent?.gift || 0}</strong> sao
                </GiftInfo>
              </GiftSection>
            </>
          )}
        </Content>

        <Footer>
          <CancelButton onClick={onClose}>Hủy</CancelButton>
          <SubmitButton onClick={handleSubmit} disabled={!selectedStudent || loading}>
            {loading ? 'Đang gửi...' : 'Gửi Lời Thách Đấu'}
          </SubmitButton>
        </Footer>
      </Modal>
    </Overlay>
  );
};

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 2px solid #e5e7eb;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #1f2937;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const SearchResults = styled.div`
  background: #f9fafb;
  border-radius: 10px;
  margin-bottom: 20px;
  max-height: 300px;
  overflow-y: auto;
  border: 2px solid #e5e7eb;
`;

const StudentResult = styled.div<{ $selected?: boolean }>`
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s;
  background: ${(props) => (props.$selected ? '#dbeafe' : 'transparent')};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f3f4f6;
  }
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
`;

const StudentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const StudentName = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
`;

const StudentClass = styled.div`
  font-size: 12px;
  color: #667eea;
  margin-top: 2px;
  font-weight: 500;
`;

const StudentGifts = styled.div`
  font-size: 12px;
  color: #9ca3af;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
`;

const LoadingText = styled.div`
  padding: 16px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
`;

const EmptyText = styled.div`
  padding: 16px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
`;

const SelectedContainer = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
  font-size: 14px;
`;

const SelectedStudent = styled.div`
  padding: 12px;
  background: #dbeafe;
  border: 2px solid #3b82f6;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const GiftSection = styled.div`
  margin-bottom: 20px;
`;

const GiftInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const GiftButton = styled.button`
  width: 40px;
  height: 40px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  font-size: 20px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const GiftInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  text-align: center;
  font-weight: 600;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const GiftInfo = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
  font-size: 13px;
  margin-bottom: 16px;
`;

const Footer = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 2px solid #e5e7eb;
  background: #f9fafb;
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #d1d5db;
    background: #f3f4f6;
  }
`;

const SubmitButton = styled.button`
  flex: 1;
  padding: 12px;
  background: #3b82f6;
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #2563eb;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }

  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
  }
`;
